import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PawLinkLogo } from "@/components/PawLinkLogo";
import { CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import Nav from "@/components/Nav";
import api from "@/lib/api";

/**
 * MagicLogin — consumes a one-tap sign-in token sent inside the Paddle
 * welcome email (see backend `_send_paddle_welcome_email`). On success we
 * hydrate the AuthContext and redirect to /home.
 *
 * Route: /magic-login?token=…  (link generated by the backend welcome email)
 * Behavior: single POST → sets cookie + returns user → navigate to /home.
 */
export default function MagicLogin() {
    const { t } = useI18n();
    const { hydrateFromReset } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [status, setStatus] = useState(token ? "loading" : "missing");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.post("/auth/magic-login", { token });
                if (cancelled) return;
                // hydrateFromReset persists the JWT + populates AuthContext.user in
                // the exact same shape the login/register flows use, so downstream
                // ProtectedRoutes let this session through immediately.
                if (typeof hydrateFromReset === "function") hydrateFromReset(data);
                setStatus("ok");
                setTimeout(() => nav("/home?magic=1"), 900);
            } catch (e) {
                if (cancelled) return;
                setStatus("error");
                setError(e?.response?.data?.detail || t("magic.error_generic"));
            }
        })();
        return () => { cancelled = true; };
    }, [token, hydrateFromReset, nav, t]);

    return (
        <div className="min-h-screen bg-paw-porcelain dark:bg-paw-charcoal text-paw-ink dark:text-paw-bone">
            <Nav />
            <div className="max-w-md mx-auto px-5 pt-24 pb-16">
                <div className="text-center mb-8">
                    <PawLinkLogo size={44} className="mx-auto mb-4" />
                    <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="magic-title">
                        {status === "ok"    && t("magic.ok_title")}
                        {status === "loading" && t("magic.loading_title")}
                        {status === "error" && t("magic.error_title")}
                        {status === "missing" && t("magic.missing_title")}
                    </h1>
                </div>

                {status === "loading" && (
                    <p className="text-center text-paw-ink2 dark:text-paw-bone/60" data-testid="magic-loading">
                        {t("magic.loading_body")}
                    </p>
                )}

                {status === "ok" && (
                    <div className="flex flex-col items-center gap-3 text-center" data-testid="magic-ok">
                        <CheckCircle size={44} weight="fill" className="text-emerald-500" />
                        <p className="text-paw-ink2 dark:text-paw-bone/70">
                            {t("magic.ok_body")}
                        </p>
                    </div>
                )}

                {(status === "error" || status === "missing") && (
                    <div className="flex flex-col items-center gap-3 text-center" data-testid="magic-error">
                        <WarningCircle size={44} weight="fill" className="text-red-500" />
                        <p className="text-paw-ink2 dark:text-paw-bone/70">
                            {error || t("magic.missing_body")}
                        </p>
                        <div className="mt-2 flex gap-3">
                            <Link
                                to="/login"
                                className="inline-flex items-center rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-5 py-2.5 text-sm font-semibold"
                                data-testid="magic-login-cta"
                            >
                                {t("magic.go_login")}
                            </Link>
                            <Link
                                to="/support"
                                className="inline-flex items-center rounded-full border border-paw-line dark:border-white/15 px-5 py-2.5 text-sm font-semibold"
                                data-testid="magic-support-cta"
                            >
                                {t("magic.get_help")}
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
